import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { ExternalLink, Calendar, Target, Lightbulb, BarChart3, Globe, Award, Users, TrendingUp, Palette, Star, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SpeedInscriCaseStudy = () => {
  const results = [
    { label: "Déploiement", value: "10 pays", subtext: "+130 partenaires et relais", icon: Globe },
    { label: "Étudiants accompagnés", value: "+1200", subtext: "inscriptions réussies", icon: Users },
    { label: "Notoriété", value: "+480%", subtext: "de visibilité organique", icon: TrendingUp },
    { label: "Taux de conversion", value: "38%", subtext: "vs 12% moyenne secteur", icon: BarChart3 },
  ];

  const additionalResults = [
    { label: "Engagement social media", value: "+350%", subtext: "d'interactions qualifiées", icon: Star },
    { label: "Recommandation", value: "92%", subtext: "des clients par bouche-à-oreille", icon: CheckCircle },
  ];

  const expertise = [
    { icon: Target, title: "Stratégie de marque et storytelling" },
    { icon: Globe, title: "Communication digitale et sociale" },
    { icon: Palette, title: "Design web et expérience utilisateur" },
    { icon: Lightbulb, title: "Growth hacking & acquisition client" },
    { icon: Users, title: "Gestion de projet digital" },
    { icon: BarChart3, title: "Partenariats B2B & développement commercial" },
  ];

  const challenges = [
    "Créer une marque forte et différenciante, dans un secteur ultra-concurrentiel",
    "Développer une plateforme digitale intuitive, multilingue et évolutive",
    "Mettre en place un réseau international de partenaires (écoles, universités, agences locales)",
    "Gagner en crédibilité et en visibilité, sans capital de départ"
  ];

  const solutions = [
    {
      title: "Création & structuration",
      icon: Target,
      items: [
        "Définition du positionnement et des personas cibles",
        "Valeurs de marque et vision long terme",
        "Charte éditoriale et ton de communication"
      ]
    },
    {
      title: "Branding & identité",
      icon: Palette,
      items: [
        "Conception du logo et charte graphique",
        "Slogan et univers visuel complet",
        "Déclinaisons print & digital"
      ]
    },
    {
      title: "Digital",
      icon: Globe,
      items: [
        "Création complète du site vitrine",
        "Formulaires intelligents et automatisations",
        "Plateforme e-learning multilingue"
      ]
    },
    {
      title: "Communication 360°",
      icon: TrendingUp,
      items: [
        "Animation des réseaux sociaux",
        "Contenus immersifs et éducatifs",
        "Campagnes géociblées"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-navy-50 to-pink-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Content */}
              <div>
                <div className="inline-flex items-center gap-2 bg-white/80 text-blue-600 px-6 py-3 rounded-full text-sm font-medium mb-8 animate-fade-in">
                  <Award size={16} />
                  BRANDING & COMMUNICATION 360°
                </div>
                
                <h1 className="text-6xl font-light text-navy-900 mb-6 leading-tight">
                  <span className="font-semibold text-blue-600">Speed-Inscri</span>
                </h1>
                
                <p className="text-2xl text-navy-600 mb-8 font-light leading-relaxed">
                  Création, développement et transformation digitale d'une startup à impact international
                </p>

                <div className="space-y-4 mb-12">
                  <div className="flex items-center gap-4 bg-white/80 p-4 rounded-lg">
                    <Calendar size={20} className="text-blue-600" />
                    <div>
                      <div className="font-semibold text-navy-900">Durée</div>
                      <div className="text-navy-600">Projet en cours (2020 - aujourd'hui)</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 bg-white/80 p-4 rounded-lg">
                    <Target size={20} className="text-blue-600" />
                    <div>
                      <div className="font-semibold text-navy-900">Rôle</div>
                      <div className="text-navy-600">Directrice de projet – Branding, Communication, Digital, Développement stratégique</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2"
                    onClick={() => window.open('https://speed-inscri.com', '_blank')}
                  >
                    Visiter le site <ExternalLink size={16} />
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    onClick={() => document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Voir les résultats <ArrowRight size={16} />
                  </Button>
                </div>
              </div>

              {/* Visual */}
              <div className="relative">
                <div className="bg-white rounded-xl shadow-2xl p-8">
                  <img 
                    src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&h=400&fit=crop&crop=faces"
                    alt="Étudiants internationaux" 
                    className="w-full h-80 object-cover rounded-lg mb-6"
                  />
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-navy-900 mb-2">La passerelle vers l'apprentissage international</h3>
                    <p className="text-navy-600">Facilitant l'accès à la mobilité étudiante pour les jeunes d'Afrique francophone</p>
                  </div>
                </div>
                
                {/* Floating cards */}
                <div className="absolute -top-6 -left-6 bg-blue-600 text-white p-4 rounded-lg shadow-lg">
                  <div className="text-2xl font-bold">1200+</div>
                  <div className="text-sm">Étudiants accompagnés</div>
                </div>
                
                <div className="absolute -bottom-6 -right-6 bg-pink-500 text-white p-4 rounded-lg shadow-lg">
                  <div className="text-2xl font-bold">10</div>
                  <div className="text-sm">Pays de déploiement</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-light text-navy-900 mb-6">
                Des <span className="font-semibold text-blue-600">résultats</span> mesurables
              </h2>
              <p className="text-lg text-navy-600 max-w-3xl mx-auto">
                Speed-Inscri est devenue une référence en accompagnement éducatif international, 
                avec des résultats concrets et mesurables.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {results.map((result, index) => (
                <div key={index} className="text-center group">
                  <div className="bg-navy-50 p-8 rounded-none shadow-sm hover:shadow-lg transition-all duration-300 group-hover:-translate-y-1">
                    <result.icon className="text-blue-600 mx-auto mb-4" size={40} />
                    <div className="text-3xl font-bold text-navy-900 mb-2">{result.value}</div>
                    <div className="text-navy-900 font-medium mb-1">{result.label}</div>
                    <div className="text-sm text-navy-500">{result.subtext}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {additionalResults.map((result, index) => (
                <div key={index} className="bg-gradient-to-r from-blue-50 to-pink-50 p-8 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-600 text-white p-4 rounded-lg">
                      <result.icon size={24} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-navy-900">{result.value}</div>
                      <div className="text-navy-900 font-medium">{result.label}</div>
                      <div className="text-navy-600">{result.subtext}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-20 bg-navy-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-light text-navy-900 mb-8">
                  Le <span className="font-semibold text-blue-600">défi</span> relevé
                </h2>
                <p className="text-lg text-navy-600 mb-8 leading-relaxed">
                  Lancer une startup dans le domaine de l'éducation internationale en pleine période de crise (Covid-19), 
                  avec une vision claire : faciliter l'accès à la mobilité étudiante et professionnelle internationale 
                  pour les jeunes d'Afrique francophone et au-delà.
                </p>
                
                <div className="space-y-4">
                  {challenges.map((challenge, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="bg-blue-600 text-white p-2 rounded-full mt-1">
                        <CheckCircle size={16} />
                      </div>
                      <p className="text-navy-600 flex-1">{challenge}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=600&h=500&fit=crop"
                  alt="Collaboration étudiante" 
                  className="w-full h-96 object-cover rounded-lg shadow-xl"
                />
                <div className="absolute inset-0 bg-blue-600/10 rounded-lg"></div>
                
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 p-6 rounded-lg shadow-lg">
                  <h3 className="font-semibold text-navy-900 mb-2">Impact Covid-19</h3>
                  <p className="text-sm text-navy-600">
                    Malgré la crise mondiale, Speed-Inscri a su s'adapter et continuer à accompagner 
                    les étudiants vers leurs rêves internationaux.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-light text-navy-900 mb-6">
                La <span className="font-semibold text-blue-600">solution</span> mise en œuvre
              </h2>
              <p className="text-lg text-navy-600 max-w-3xl mx-auto">
                J'ai piloté l'ensemble du projet de A à Z, en mobilisant toutes mes expertises 
                en stratégie, communication, digital, UX, branding, et développement.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {solutions.map((solution, index) => (
                <div key={index} className="group">
                  <div className="bg-navy-50 p-8 rounded-none shadow-sm hover:shadow-lg transition-all duration-300 h-full">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="bg-blue-600 text-white p-4 rounded-none group-hover:bg-pink-500 transition-colors duration-300">
                        <solution.icon size={24} />
                      </div>
                      <h3 className="text-xl font-semibold text-navy-900">{solution.title}</h3>
                    </div>
                    
                    <ul className="space-y-3">
                      {solution.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-3">
                          <CheckCircle size={16} className="text-blue-600 mt-1 flex-shrink-0" />
                          <span className="text-navy-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visual Impact Section */}
      <section className="py-20 bg-gradient-to-r from-navy-900 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1200&h=600&fit=crop"
            alt="Éducation digitale" 
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-light mb-8">
              Un <span className="font-semibold text-blue-400">impact</span> international
            </h2>
            
            <p className="text-xl mb-12 leading-relaxed opacity-90">
              Speed-Inscri est devenue une référence en accompagnement éducatif international, 
              alliant innovation, proximité, rigueur et créativité.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
                <div className="text-3xl font-bold text-blue-400 mb-2">130+</div>
                <div className="text-white">Partenaires internationaux</div>
              </div>
              <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
                <div className="text-3xl font-bold text-blue-400 mb-2">92%</div>
                <div className="text-white">Taux de satisfaction</div>
              </div>
              <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
                <div className="text-3xl font-bold text-blue-400 mb-2">38%</div>
                <div className="text-white">Taux de conversion</div>
              </div>
            </div>
            
            <div className="bg-white/10 p-8 rounded-lg backdrop-blur-sm">
              <p className="text-lg mb-6">
                Aujourd'hui, la startup vise l'ouverture d'un centre de formation hybride (présentiel + digital) 
                et le développement d'un service d'accompagnement premium.
              </p>
              <Button 
                className="bg-white text-navy-900 hover:bg-gray-100"
                onClick={() => window.open('https://speed-inscri.com', '_blank')}
              >
                Découvrir Speed-Inscri <ExternalLink size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-navy-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-light text-navy-900 mb-6">
                Expertises <span className="font-semibold text-blue-600">mobilisées</span>
              </h2>
              <p className="text-lg text-navy-600 max-w-2xl mx-auto">
                Une approche complète et stratégique pour un projet d'envergure internationale
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {expertise.map((item, index) => (
                <div key={index} className="text-center group animate-fade-in" style={{animationDelay: `${index * 0.1}s`}}>
                  <div className="bg-white p-8 rounded-none shadow-sm hover:shadow-lg transition-all duration-300 group-hover:-translate-y-1 h-full">
                    <div className="bg-blue-600 text-white p-4 rounded-none w-16 h-16 mx-auto mb-6 flex items-center justify-center group-hover:bg-pink-500 transition-colors duration-300">
                      <item.icon size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-navy-900 group-hover:text-blue-600 transition-colors duration-300">
                      {item.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-light text-navy-900 mb-6">
              Votre projet mérite une approche
              <span className="font-semibold text-blue-600"> d'exception</span>
            </h2>
            <p className="text-xl text-navy-600 mb-8 font-light">
              Discutons de vos objectifs et créons ensemble une stratégie qui vous démarque.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:julycia.pro@gmail.com"
                className="bg-blue-600 text-white px-8 py-4 font-medium hover:bg-blue-700 transition-colors duration-300 group inline-flex items-center justify-center gap-2"
              >
                Démarrer un projet
                <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <button 
                onClick={() => {
                  const phoneNumber = "+33643581540";
                  const message = "Bonjour Julycia ! J'aimerais discuter d'un projet de communication avec vous après avoir vu le cas Speed-Inscri.";
                  const whatsappUrl = `https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="bg-green-500 text-white px-8 py-4 font-medium hover:bg-green-600 transition-colors duration-300 group inline-flex items-center justify-center gap-2"
              >
                Discuter sur WhatsApp
                <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default SpeedInscriCaseStudy; 